<template>
  <!-- Inline, not centred in its own block: this button now sits in the summary's action row with the
       rest of them. -->
  <span>
    <button class="btn btn-link" @click="showRepairLinesModal">
      <i class="fa-solid fa-list-check me-1"></i>Repair Lines
    </button>
  </span>
  <DialogPreview :show="showRepairLines" modalSize="modal-xl">
    <template v-slot:header>
      <div class="row" style="width: 100%">
        <div class="col-12">
          <span class="custom-title">Repair Lines</span>
          <span class="float-end" data-bs-dismiss="modal" @click="closeModal">
            <i class="fa fa-close" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </template>
    <template v-slot:body>
      <div class="d-none d-md-block">
        <ZTable
          v-model="repairLineData"
          class="table table-bordered table-striped"
          :is-single-row-detail="false"
          :is-header-fixed="true"
          :columns="tableHeader"
        >
          <template #tbody="props">
            <td :class="props.row.isCalibrate + ''" v-if="props.row.isCalibrate === true">
              <a
                v-popover
                tab-index="0"
                data-bs-toggle="popover"
                data-bs-trigger="focus hover"
                data-bs-html="true"
                data-bs-sanitize="false"
                title="Calibration Findings"
                :data-bs-content="groupNotice(props.row.calibrateWhy)"
                class="btn btn-link"
                role="button"
              >
                <i class="fa-solid fa-location-crosshairs"></i>
              </a>
              {{ props.row.lineNum }}
            </td>
            <td v-else>
              {{ props.row.lineNum }}
            </td>
            <!-- The group cell was rendered empty, so every row's section heading was lost. -->
            <td>{{ props.row.lineHeader }}</td>
            <td>{{ props.row.lineDescription }}</td>
            <td>{{ props.row.partLines ? props.row.partLines[0]['partTypeName'] : null }}</td>
            <td>{{ partNumber(props.row) }}</td>
            <td>{{ props.row.partLines ? props.row.partLines[0]['quantity'] : null }}</td>
            <td>{{ bodyLabor(props.row, 'laborTypeName') }}</td>
            <td>{{ bodyLabor(props.row, 'laborOperationName') }}</td>
            <!-- Body and refinish hours are separate labor rows, so showing only the first hid one of
                 them; "Incl." is shown as itself rather than as a blank that reads like zero. -->
            <td>{{ hours(bodyLaborLine(props.row)) }}</td>
            <td>{{ hours(paintLaborLine(props.row)) }}</td>
          </template>
          <template #empty> Nothing found! </template>
        </ZTable>
      </div>
      <div class="d-block d-md-none">
        <div class="shop-card">
          <div class="row m-0 dashboard-shop-card">
            <div
              v-for="(item, idx) of repairLineData"
              :key="idx"
              :class="item.isCalibrate + ' col-12 col-sm-12 shop-list-container p-2'"
            >
              <div class="shop-list-div p-3">
                <div
                  class="shop-header-mobile pb-3 d-flex justify-content-between align-items-center"
                >
                  <div class="d-flex align-items-center">
                    <p class="m-0" v-if="item.isCalibrate === true">
                      <a
                        v-popover
                        tab-index="0"
                        data-bs-toggle="popover"
                        data-bs-trigger="focus hover"
                        data-bs-html="true"
                        data-bs-sanitize="false"
                        title="Calibration Findings"
                        :data-bs-content="groupNotice(item.calibrateWhy)"
                        class="btn btn-link"
                        role="button"
                      >
                        <i class="fa-solid fa-location-crosshairs"></i>
                      </a>
                    </p>
                    <p class="shop-name-mobile colorPrimary text-start fw-6 fs-16 m-0">
                      {{ item.lineDescription }}
                    </p>
                  </div>
                  <div>
                    <p class="shop-name-mobile colorPrimary text-start fw-6 fs-16 m-0">
                      #{{ item.lineNum }}
                    </p>
                  </div>
                </div>
                <div class="shop-body-mobile d-flex justify-content-between pt-3">
                  <div>
                    <p class="text-start colorPrimary fs-12 m-0">Group</p>
                    <p class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0">
                      NA
                    </p>
                  </div>
                  <div>
                    <p class="text-start fs-12 colorPrimary m-0">Qty/Hrs</p>
                    <p
                      v-if="item.partLines"
                      class="shop-service-completed-mobile text-start fs-14 colorPrimary fw-b m-0"
                    >
                      {{ item.partLines ? item.partLines[0]['quantity'] : null }}
                    </p>
                    <p v-else>NA</p>
                  </div>
                </div>
                <div class="shop-body-mobile d-flex justify-content-between pt-3">
                  <div>
                    <p class="text-start colorPrimary fs-12 m-0">Labour</p>
                    <p
                      v-if="item.laborLines"
                      class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0"
                    >
                      {{ item.laborLines ? item.laborLines[0]['laborTypeName'] : null }}
                    </p>
                    <p v-else>NA</p>
                  </div>
                  <div>
                    <p class="text-start colorPrimary fs-12 m-0">Operation</p>
                    <p
                      v-if="item.laborLines"
                      class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0"
                    >
                      {{ item.laborLines[0]['laborOperationName'] }}
                    </p>
                    <p v-else>NA</p>
                  </div>
                </div>
                <div class="shop-body-mobile d-flex justify-content-between pt-3">
                  <div>
                    <p class="text-start colorPrimary fs-12 m-0">Hrs</p>
                    <p
                      v-if="item.laborLines"
                      class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0"
                    >
                      {{ item.laborLines[0]['laborHours'] }}
                    </p>
                    <p v-else>NA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <button
        type="button"
        class="secondaryButton px-4"
        data-bs-dismiss="modal"
        @click="closeModal"
      >
        Okay
      </button>
    </template>
  </DialogPreview>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { adasStore } from '@/stores/adas'
import { dashboardStore } from '@/stores/dashboard'
import DialogPreview from '@/components/utilities/elements/Dialog.vue'
import ZTable from '@/components/utilities/elements/Table.vue'
export default {
  name: 'RepairLines',
  components: {
    DialogPreview,
    ZTable
  },
  data() {
    return {
      showRepairLines: null,
      linesdata: null,
      headerProps: {
        pageSizes: [
          { name: '10', value: 10 },
          { name: '25', value: 25 },
          { name: '50', value: 50 },
          { name: '100', value: 100 }
        ],
        title: null,
        subtitle: null,
        isTitle: false,
        isPaginated: true,
        isChecked: false,
        isAdd: false,
        isDelete: false,
        toggleButtons: null,
        buttons: null
      },
      tableHeader: [
        { field: 'number', label: '#', sortable: false },
        { field: 'Group', label: 'Group', sortable: false },
        { field: 'Description', label: 'Description', sortable: false },
        { field: 'Part/Service', label: 'Part/Service', sortable: false },
        { field: 'PartNumber', label: 'Part #', sortable: false },
        { field: 'QTY', label: 'QTY', sortable: false },
        { field: 'Labour', label: 'Labor', sortable: false },
        { field: 'Op', label: 'Op', sortable: false },
        { field: 'Hrs', label: 'Hrs', sortable: false },
        { field: 'Paint', label: 'Paint', sortable: false }
      ],
      currentParams: {
        numItems: 0,
        page: 1,
        pageSize: 10,
        filterBy: null,
        searchTerm: '',
        orderBy: 'adasInspection',
        sortBy: 'asc'
      }
    }
  },
  mounted() {
    this.init()
  },
  computed: {
    ...mapState(dashboardStore, ['firm']),
    ...mapState(adasStore, ['repairLines', 'inspectionInfo']),
    source() {
      let _data = null
      if (this.firm) {
        _data = this.firm.source
      }
      return _data
    },
    repairLineData() {
      let _data = null
      if (this.repairLines) {
        _data = this.repairLines
      }
      return _data
    }
  },
  methods: {
    ...mapActions(adasStore, ['getRepairLines']),
    // Refinish is saved as its own labor row (LAR / Operation - Refinish), so the two are picked apart
    // here rather than showing whichever happened to be written first.
    paintLaborLine(row) {
      const lines = row && row.laborLines ? row.laborLines : []
      return lines.find((l) => l.laborType === 'LAR' || l.laborOperation === 'OP6') || null
    },
    bodyLaborLine(row) {
      const lines = row && row.laborLines ? row.laborLines : []
      return lines.find((l) => l !== this.paintLaborLine(row)) || null
    },
    bodyLabor(row, field) {
      const line = this.bodyLaborLine(row)
      return line ? line[field] : null
    },
    // "Incl." is a value, not an absence: an estimate that says the labor is included in another line
    // must not read as 0.0 hours.
    hours(line) {
      if (!line) return null
      if (line.isLaborIncluded) return 'Incl.'
      return line.laborHours
    },
    partNumber(row) {
      const parts = row && row.partLines ? row.partLines : []
      const withNumber = parts.find((p) => p && p.partNumber)
      if (withNumber) return withNumber.partNumber
      return parts.length && parts[0].isPriceIncluded ? 'Incl.' : null
    },
    async init() {
      await this.getRepairLines(this.source, this.inspectionInfo.adas, this.inspectionInfo.adasInspection)
    },

    showRepairLinesModal() {
      this.showRepairLines = true
    },
    closeModal() {
      this.showRepairLines = false
    },
    groupNotice(content) {
      let trigger = content
      trigger = trigger.replace(/&comma;/g, ',')
      let _data = `<div class='popover' role='tooltip' style='overflow-x: hidden !important;  overflow-y: auto !important;  max-height: 200px !important;'><div class='popover-body' >${trigger}</div></div>`
      return _data
    }
  }
}
</script>
<style lang="scss" scoped>
.dashboard-shop-card {
  max-height: 560px;
  overflow: auto;
}
.shop-list-div:nth-child(even) {
  background-color: #fcfcfd;
}
.true .shop-list-div {
  background-color: yellow;
}

.shop-list-div:hover {
  background-color: rgb(97 169 36 / 10%);
  cursor: pointer;
}
.shop-list-div {
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 10px;
}
.shop-header-mobile {
  border-bottom: 1px solid var(--bs-border-color-translucent);
}
tr td.true {
  background-color: yellow;
}
</style>
